import { gql } from '@apollo/client'
import { BodyText, Caption, Heading, RadioCard } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import { useEffect, useState } from 'react'

import { CheckoutButton } from '../checkout-button'
import { OneClickButton } from '../one-click-button'
import {
  CaseTrackingBgCheckFromLawsuitOffersContent_OfferLockDataFragment,
  CaseTrackingBgCheckFromLawsuitOffersContent_ViewerFragment,
} from './__generated__/case-tracking-bg-check-from-lawsuit-offers-content.graphql'
import styles from './case-tracking-bg-check-from-lawsuit-offers.module.scss'
import PlanOfferCards from './plan-offer-cards'
import SinglePlanCard from './single-plan-card'

export interface CaseTrackingBGCheckFromLawsuitOffersContentProps {
  offerLockData: CaseTrackingBgCheckFromLawsuitOffersContent_OfferLockDataFragment
  viewer: CaseTrackingBgCheckFromLawsuitOffersContent_ViewerFragment
}

type Card =
  CaseTrackingBgCheckFromLawsuitOffersContent_OfferLockDataFragment['body']['cards'][number]

export function CaseTrackingBGCheckFromLawsuitOffersContent({
  offerLockData,
  viewer,
}: CaseTrackingBGCheckFromLawsuitOffersContentProps) {
  const header = offerLockData.header
  const { title = '', subtitle = '' } = header
  const { body, footer } = offerLockData || {}
  const { cards } = body || {}
  const [selectedCard, setSelectedCard] = useState<Card | null>(cards?.[0] || null)
  const currentPlanPriceText = selectedCard?.offer.currentPlanPriceText || ''
  const frequency = selectedCard?.offer.frequencyPeriodText
  const hasSingleCard = cards.length === 1

  useEffect(() => {
    if (hasSingleCard && cards[0]) {
      setSelectedCard(cards[0])
    }
  }, [hasSingleCard, cards])

  return (
    <>
      <Heading
        size="lg"
        className={styles.caseTrackingBgCheckFromLawsuitOffersTitle}
        data-testid="case-tracking-bg-check-from-lawsuit-offers-content"
      >
        {title}
      </Heading>
      <BodyText size="md" className={styles.caseTrackingBgCheckFromLawsuitOffersSubtitle}>
        <span dangerouslySetInnerHTML={{ __html: `${subtitle}` }} />
      </BodyText>
      {hasSingleCard ? (
        <SinglePlanCard offerLockData={offerLockData} />
      ) : (
        <div className={styles.caseTrackingBgCheckFromLawsuitOffersCards}>
          <RadioCard.Group
            defaultValue="0"
            name="case-tracking-bg-check-from-lawsuit-offers-group"
            onValueChange={(value) => setSelectedCard(cards[Number(value)])}
            className={styles.caseTrackingBgCheckFromLawsuitOffersRadioCardGroup}
          >
            <PlanOfferCards offerLockData={offerLockData} />
          </RadioCard.Group>
        </div>
      )}
      {selectedCard && (
        <div className={styles.caseTrackingBgCheckFromLawsuitOffersPrice}>
          <Price align="center" size="lg">
            <Price.Discount className={styles.priceDiscountText}>
              <span
                className={styles.currentPlanPriceText}
                dangerouslySetInnerHTML={{ __html: currentPlanPriceText }}
              />
            </Price.Discount>

            <Price.Value>
              <span className={styles.planContainerPriceText}>{selectedCard?.offer.priceText}</span>

              <Price.ValueFrequency>
                <span className={styles.frequencyPeriodText}>{frequency}</span>
              </Price.ValueFrequency>
            </Price.Value>
          </Price>
        </div>
      )}
      <div className={styles.caseTrackingBgCheckFromLawsuitOffersPaymentButtons}>
        <CheckoutButton
          forceDisabled={!selectedCard}
          offerLockCard={selectedCard || cards[0]}
          kind="primary"
          viewer={viewer}
        />
        <OneClickButton
          forceDisabled={!selectedCard}
          offerLockCard={selectedCard || cards[0]}
          viewer={viewer}
          kind="primary"
        />
      </div>
      {footer.text && (
        <div className={styles.caseTrackingBgCheckFromLawsuitOffersFooter}>
          <Caption>
            <p dangerouslySetInnerHTML={{ __html: footer?.text || '' }} />
          </Caption>
        </div>
      )}
    </>
  )
}

CaseTrackingBGCheckFromLawsuitOffersContent.fragments = {
  offerLockData: gql`
    fragment CaseTrackingBGCheckFromLawsuitOffersContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      ...PlanOfferCards_offerLockData
      ...SinglePlanCard_offerLockData
      body {
        cards {
          offer {
            frequencyPeriodText
            currentPlanPriceText
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
        }
      }
      footer {
        text
      }
    }
    ${PlanOfferCards.fragments.offerLockData}
    ${SinglePlanCard.fragments.offerLockData}
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment CaseTrackingBGCheckFromLawsuitOffersContent_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
